import { useState, useEffect, useCallback, useRef } from 'react';
import useAccess from '../../../Hooks/useAccess';

interface SubscriptionLimitState {
  // Data
  limit: number | null;
  processed: number | null;
  remaining: number | null;
  
  // States
  isLoading: boolean;
  error: string | null;
  lastUpdated: number | null;
  
  // Computed
  isLimitReached: boolean;
  isApproachingLimit: boolean;
  isAtExactLimit: boolean;
  hasValidData: boolean;
}

interface LimitCheckResult {
  shouldBlock: boolean;
  reason?: string;
}

interface UseSubscriptionLimitReturn extends SubscriptionLimitState {
  // Actions
  checkLimit: (mode: 'new' | 'edit' | 'clone') => LimitCheckResult;
  refreshSubscription: () => Promise<void>;
  clearError: () => void;
}

const SUBSCRIPTION_CACHE_DURATION = 30000; // 30 seconds
const APPROACHING_LIMIT_THRESHOLD = 2;
const SUBSCRIPTION_LOAD_TIMEOUT = 5000; // 5 seconds

export const useSubscriptionLimit = (): UseSubscriptionLimitReturn => {
  const { currentLimits, currentSubscription } = useAccess();
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Normalize data with explicit handling
  const limit = currentLimits?.jobPosts ?? null;
  const processed = currentSubscription?.measurements?.jobPosts ?? null;

  // DEBUG: log every time subscription data changes
  console.log('[SubscriptionLimit] Data snapshot:', {
    limit,
    processed,
    isLoading,
    currentLimits,
    measurements: currentSubscription?.measurements,
    calledAt: new Date().toISOString(),
    stack: new Error().stack?.split('\n').slice(1, 5).join(' | '),
  });
  
  // Calculate derived values
  const remaining = 
    limit !== null && processed !== null && limit > 0
      ? Math.max(0, limit - processed)
      : null;
  
  const hasValidData = limit !== null && processed !== null;
  
  // Business logic: limit = 0 means blocked (no job posts allowed)
  // limit = null means unlimited (no limit set)
  const isLimitReached = 
    limit !== null && 
    limit >= 0 && // Explicitly handle 0 as blocked
    processed !== null && 
    processed >= limit;
  
  const isAtExactLimit = 
    limit !== null && 
    processed !== null && 
    limit > 0 &&
    processed === limit;
  
  const isApproachingLimit = 
    remaining !== null && 
    remaining > 0 && 
    remaining <= APPROACHING_LIMIT_THRESHOLD;

  // Refresh subscription data
  const refreshSubscription = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      // Set timeout for loading state
      timeoutRef.current = setTimeout(() => {
        if (!currentLimits && !currentSubscription) {
          setIsLoading(false);
          setError('Subscription data unavailable. Please check your connection.');
        }
      }, SUBSCRIPTION_LOAD_TIMEOUT);
      
      // Note: useSubscription hook handles actual fetch
      // We track state and provide timeout handling
      
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to refresh subscription';
      setError(errorMsg);
      setIsLoading(false);
      console.error('Subscription refresh error:', err);
    }
  }, [currentLimits, currentSubscription]);

  // Check limit with comprehensive logic
  const checkLimit = useCallback((mode: 'new' | 'edit' | 'clone'): LimitCheckResult => {
    console.log('[SubscriptionLimit] checkLimit() called:', {
      mode,
      limit,
      processed,
      isLimitReached,
      hasValidData,
      isLoading,
      lastUpdated,
    });

    // Edit mode: Never block
    if (mode === 'edit') {
      return { shouldBlock: false };
    }

    // If still loading and we have cached data, use cache
    if (isLoading && lastUpdated) {
      const cacheAge = Date.now() - (lastUpdated || 0);
      if (cacheAge < SUBSCRIPTION_CACHE_DURATION) {
        // Use cached data for check (continue below)
      } else {
        // Cache expired, but allow with warning
        return {
          shouldBlock: false,
          reason: 'Subscription data is loading. Please verify your limit before publishing.'
        };
      }
    }

    // If subscription data unavailable after timeout
    if (error && !hasValidData) {
      return {
        shouldBlock: false,
        reason: 'Unable to verify subscription limit. Proceeding with caution.'
      };
    }

    // If no valid data but no error (initial state)
    if (!hasValidData && !error) {
      // Still loading - allow with warning if timeout
      if (isLoading) {
        return {
          shouldBlock: false,
          reason: 'Verifying subscription status...'
        };
      }
      // Not loading but no data - allow (unlimited)
      return {
        shouldBlock: false,
        reason: 'No subscription limit found. Proceeding.'
      };
    }

    if(limit === -1) {
      return {
        shouldBlock: false,
        reason: 'Unlimited job posts. Proceeding.'
      };
    }
    // Special case: limit = 0 means blocked
    if (limit === 0) {
      return {
        shouldBlock: true,
        reason: 'Your plan does not allow job posts. Please upgrade your subscription.'
      };
    }

    // Normal case: Check if limit reached
    if (isLimitReached) {
      return {
        shouldBlock: true,
        reason: `You have reached your limit of ${limit} job post${limit === 1 ? '' : 's'}. Please upgrade to create more.`
      };
    }

    // All checks passed - allow
    return { shouldBlock: false };
  }, [
    isLoading,
    lastUpdated,
    error,
    hasValidData,
    limit,
    isLimitReached
  ]);

  // Clear error manually
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Initialize on mount
  useEffect(() => {
    // Set initial loading state
    setIsLoading(true);
    
    // Check if data is already available
    if (currentLimits || currentSubscription) {
      setLastUpdated(Date.now());
      setIsLoading(false);
    } else {
      // Wait for data with timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      timeoutRef.current = setTimeout(() => {
        if (!currentLimits && !currentSubscription) {
          setIsLoading(false);
          setError('Subscription data not available');
        }
      }, SUBSCRIPTION_LOAD_TIMEOUT);
    }
    
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Update lastUpdated when data changes
  useEffect(() => {
    if (currentLimits || currentSubscription) {
      setLastUpdated(Date.now());
      setIsLoading(false);
      setError(null);
      
      // Clear timeout if data arrives
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  }, [currentLimits, currentSubscription]);

  return {
    // Data
    limit,
    processed,
    remaining,
    
    // States
    isLoading,
    error,
    lastUpdated,
    
    // Computed
    isLimitReached,
    isApproachingLimit,
    isAtExactLimit,
    hasValidData,
    
    // Actions
    checkLimit,
    refreshSubscription,
    clearError,
  };
};

